/**
 * OutlookShell
 *
 * Layout (no top bar — uses the global app header):
 *   ┌─────────────────────────────────────────────────────────┐
 *   │  Ribbon (mail controls + inline search)                 │
 *   ├──────────────┬───────────────┬─────────────────────────┤
 *   │ Folder       │  Email List   │  Reading Pane            │
 *   │ Sidebar      │  (320px)      │  — or —                  │
 *   │ (240px)      │               │  Compose Panel           │
 *   └──────────────┴───────────────┴─────────────────────────┘
 *
 * When compose is open, the right pane shows <ComposeMail> full-width.
 * No modal/popup/overlay — compose is always inline.
 *
 * Notifications are surfaced via the global Header's NotificationBell (notify.js)
 * which reads from mailUiSlice.notificationHistory.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import MailFolderSidebar     from './MailFolderSidebar';
import EmailList             from './EmailList';
import ReadingPane           from './ReadingPane';
import ComposeMail           from './ComposeMail';
import Ribbon                from './Ribbon';
import PopoutComposeWindow   from './PopoutComposeWindow';
import ScheduledMailView     from './ScheduledMailView';

import {
  selectSelectedThreadId,
  selectSelectedFolderId,
  setInlineComposeSession,
  clearInlineCompose,
  selectDraftEditMessageId,
  clearDraftEdit,
} from '../../../Slices/mailUiSlice';
import { fetchMessageBody } from '../../../Slices/mailMessagesSlice';

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const T = {
  sidebarBg:     '#FFFFFF',
  sidebarBorder: '#E1DFDD',
  listBg:        '#FFFFFF',
  listBorder:    '#E1DFDD',
  rpBg:          '#FAF9F8',
  font:          "'Segoe UI', Tahoma, sans-serif",
};

// ---------------------------------------------------------------------------
// FolderSidebar
// ---------------------------------------------------------------------------

function FolderSidebar({ userEmail }) {
  return (
    <div style={{
      width: 240, flexShrink: 0,
      display: 'flex', flexDirection: 'column',
      background: T.sidebarBg,
      borderRight: `1px solid ${T.sidebarBorder}`,
      height: '100%',
    }}>
      <MailFolderSidebar userEmail={userEmail} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmptyReadingPane
// ---------------------------------------------------------------------------

function EmptyReadingPane() {
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      color: '#8a8886', fontFamily: T.font, gap: 10,
      background: T.rpBg, userSelect: 'none',
    }}>
      <svg width="72" height="72" viewBox="0 0 24 24" fill="none"
        stroke="#C8C6C4" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <polyline points="2,4 12,13 22,4" />
      </svg>
      <span style={{ fontSize: 15, color: '#323130' }}>Select an item to read</span>
      <span style={{ fontSize: 13, color: '#8a8886' }}>Nothing is selected</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// buildInitialValues
// ---------------------------------------------------------------------------

function buildInitialValues(type, message, fromEmail) {
  if (!message) return { fromEmail };
  const subject  = message.subject || '';
  const body     = message.body?.content || '';
  const fromAddr = message.from?.email ? [message.from.email] : [];
  const toAddrs  = (message.toRecipients || []).map(r => r.email).filter(Boolean);
  const ccAddrs  = (message.ccRecipients  || []).map(r => r.email).filter(Boolean);

  const conversationId = message.conversationId || null;
  const folderId       = message.folderId       || null;

  const bccAddrs  = (message.bccRecipients  || []).map(r => r.email).filter(Boolean);

  switch (type) {
    case 'reply':
      return { to: fromAddr, subject, body, replyToMessageId: message.id, fromEmail, conversationId, folderId };
    case 'replyAll':
      return {
        to: [...fromAddr, ...toAddrs].filter(e => e !== fromEmail),
        cc: ccAddrs.filter(e => e !== fromEmail),
        subject, body, replyToMessageId: message.id, fromEmail, conversationId, folderId,
      };
    case 'forward':
      return {
        to: [], subject, body, replyToMessageId: message.id, fromEmail, conversationId, folderId,
        originalMessage: {
          from:           message.from,
          sentDateTime:   message.sentDateTime || message.receivedDateTime || null,
          toRecipients:   message.toRecipients || [],
        },
      };
    case 'editDraft':
      // Restore all draft fields. draftId seeds draftIdRef so saves PATCH, not POST.
      return {
        to:       toAddrs,
        cc:       ccAddrs,
        bcc:      bccAddrs,
        subject,                    // preserve exactly — no RE:/FW: prefix added
        body:     message.body?.content || '',
        draftId:  message.id,
        fromEmail,
      };
    default:
      return { fromEmail };
  }
}

// ---------------------------------------------------------------------------
// OutlookShell
// ---------------------------------------------------------------------------

export default function OutlookShell({ userEmail }) {
  const dispatch    = useDispatch();
  const threadId    = useSelector(selectSelectedThreadId);
  const folderId    = useSelector(selectSelectedFolderId);
  const isScheduled = folderId === '__scheduled__';

  // composeSession: null | { type, message? }
  const [composeSession, setComposeSession] = useState(null);

  const DEFAULT_COMPOSE_OPTIONS = { showBcc: false, requestReadReceipt: false, requestDeliveryReceipt: false, importance: 'normal', sensitivity: 'normal' };
  const [composeOptions, setComposeOptions] = useState(DEFAULT_COMPOSE_OPTIONS);

  // popoutSession: null | { composeType, initialValues, fileList }
  const [popoutSession, setPopoutSession] = useState(null);

  // Draft editing — open compose when body is ready
  const draftEditMessageId = useSelector(selectDraftEditMessageId);
  const draftMessage       = useSelector(state =>
    draftEditMessageId ? state.mailMessages?.byId?.[draftEditMessageId] : null
  );
  const draftOpenedRef = useRef(null);

  const openCompose = useCallback((opts) => {
    const key     = Date.now();
    const session = { ...(opts || { type: 'new' }), _key: key };
    setComposeSession(session);
    setComposeOptions(DEFAULT_COMPOSE_OPTIONS);
    dispatch(setInlineComposeSession(`session_${key}`));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeCompose = useCallback(() => {
    setComposeSession(null);
    dispatch(clearInlineCompose());
    dispatch(clearDraftEdit());
  }, [dispatch]);

  // Watch for a pending draft-edit request. Dispatch fetchMessageBody if needed,
  // then open ComposeMail once the body is loaded.
  useEffect(() => {
    if (!draftEditMessageId) {
      draftOpenedRef.current = null;
      return;
    }
    if (!draftMessage) return;
    // Guard: don't open twice for the same message
    if (draftOpenedRef.current === draftEditMessageId) return;

    if (draftMessage.bodyStatus !== 'loaded') {
      dispatch(fetchMessageBody({ messageId: draftEditMessageId }));
      return;
    }

    // Body ready — open compose and clear the pending request
    draftOpenedRef.current = draftEditMessageId;
    dispatch(clearDraftEdit());
    openCompose({ type: 'editDraft', message: draftMessage });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftEditMessageId, draftMessage?.bodyStatus]);

  const handlePopout = useCallback(({ composeType, initialValues, fileList }) => {
    setPopoutSession({ composeType, initialValues, fileList });
    setComposeSession(null);
    dispatch(clearInlineCompose());
  }, [dispatch]);

  const closePopout = useCallback(() => {
    setPopoutSession(null);
  }, []);

  const isComposing = !!composeSession;

  return (
    <>
      <style>{SHELL_CSS}</style>

      <div style={{
        position: 'fixed', top: 98, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden', zIndex: 10, fontFamily: T.font,
      }}>
        {/* Ribbon — mail controls + inline search; auto-switches to ComposeRibbon */}
        <Ribbon onOpenCompose={openCompose} composeOptions={composeOptions} onOptionsChange={setComposeOptions} />

        {/* Main row */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', minHeight: 0 }}>

          {/* Folder Sidebar */}
          <FolderSidebar userEmail={userEmail} />

          {/* Email List — hidden when Scheduled view is active */}
          {!isScheduled && (
            <div style={{
              width: 320, flexShrink: 0,
              display: 'flex', flexDirection: 'column',
              borderRight: `1px solid ${T.listBorder}`,
              background: T.listBg, height: '100%', overflow: 'hidden',
            }}>
              <EmailList />
            </div>
          )}

          {/* Right pane: ScheduledView | Compose | ReadingPane | EmptyState */}
          <div style={{
            flex: 1, minWidth: 0, minHeight: 0,
            display: 'flex', flexDirection: 'column',
            background: isComposing ? '#fff' : T.rpBg,
            overflow: 'hidden',
          }}>
            {isScheduled ? (
              <ScheduledMailView />
            ) : isComposing ? (
              <ComposeMail
                key={composeSession._key}
                composeType={composeSession.type}
                initialValues={buildInitialValues(composeSession.type, composeSession.message, userEmail)}
                onClose={closeCompose}
                onSent={closeCompose}
                onPopout={handlePopout}
                composeOptions={composeOptions}
              />
            ) : threadId ? (
              <ReadingPane onOpenCompose={openCompose} userEmail={userEmail} />
            ) : (
              <EmptyReadingPane />
            )}
          </div>

          {/* Pop-out compose window */}        
          {popoutSession && (
            <PopoutComposeWindow
              key={`popout_${popoutSession.initialValues?.subject || Date.now()}`}
              composeType={popoutSession.composeType}
              initialValues={popoutSession.initialValues}
              onClose={closePopout}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// CSS
// ---------------------------------------------------------------------------

const SHELL_CSS = `
  .outlook-search .ant-input::placeholder { color: rgba(0,0,0,0.35) !important; }
  .outlook-search:hover,
  .outlook-search:focus-within {
    background: #fff !important;
    box-shadow: 0 0 0 2px #c7e0f4 !important;
    border-color: #0f6cbd !important;
  }
  .outlook-search .ant-input { background: transparent !important; }
`;
